import { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { useAtomValue } from "jotai";
import { DataChatMessage, postDataChatFeedback } from "../api/dataChat";
import { AppAtoms } from "../lib/store";
import { CONST } from "../config/const";
import ChartRenderer from "./chart/ChartRenderer";
import { ChartData, ChartType } from "./chart/types";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function parseChartData(wsData: Record<string, unknown>): ChartData {
  const chartType = (wsData.chart_type as ChartType) ?? "table";
  const chartConfig = (wsData.chart_config ?? {}) as Record<string, unknown>;
  const rawData = wsData.data as Record<string, unknown>[] | null;

  let rows: Record<string, unknown>[];
  if (rawData && rawData.length > 0) {
    rows = rawData;
  } else if (chartType === "kpi" && chartConfig.value != null) {
    rows = [{ value: chartConfig.value }];
  } else {
    rows = [];
  }

  const columns =
    rows.length > 0
      ? Object.keys(rows[0])
      : (chartConfig.columns as { key: string }[] | undefined)?.map(
          (c) => c.key
        ) ?? [];

  const xKey = chartConfig.x_key as string | undefined;
  const yKey = chartConfig.y_key as string | undefined;
  const yKeysArr = chartConfig.y_keys as string[] | undefined;
  const yKeys = (yKeysArr && yKeysArr.length > 0) ? yKeysArr : yKey ? [yKey] : undefined;
  const title = chartConfig.title as string | undefined;

  return { chartType, rows, columns, title, xKey, yKeys };
}

function DataChatPanel() {
  const [messages, setMessages] = useState<DataChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [chatLogId, setChatLogId] = useState<number | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const wsRef = useRef<WebSocket | null>(null);
  const streamingAnswerRef = useRef<string>("");
  const restoredRef = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // localStorage 復元（loginUser?.username 到着後）
  useEffect(() => {
    if (restoredRef.current) return;
    const username = loginUser?.username;
    if (!username) return;
    const key = "poDataChatHistory_" + username;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        restoredRef.current = true;
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setMessages(parsed);
      } else {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
    restoredRef.current = true;
  }, [loginUser?.username]);

  // localStorage 保存（messages変化時）
  useEffect(() => {
    if (!restoredRef.current) return;
    const username = loginUser?.username;
    if (!username) return;
    const key = "poDataChatHistory_" + username;
    const toSave = messages.slice(-20).map((m) => ({
      role: m.role,
      content: m.content.slice(0, 2000),
    }));
    localStorage.setItem(key, JSON.stringify(toSave));
  }, [messages, loginUser?.username]);

  const handleSend = useCallback(
    (overrideMsg?: string) => {
      const msg = overrideMsg ?? inputValue;
      if (!msg.trim()) return;
      setSuggestedQuestions([]);

      const userMsg: DataChatMessage = { role: "user", content: msg };
      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setLoading(true);
      streamingAnswerRef.current = "";

      const idToken = loginUser?.idToken;
      if (!idToken) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "認証情報が取得できません。再ログインしてください。",
          },
        ]);
        setLoading(false);
        return;
      }

      // history 組立: user→直後の content 非空 assistant のみペア化・error定型文除外・直近3件・古い順
      const pairs: { question: string; answer: string }[] = [];
      let hi = 0;
      while (hi < messages.length) {
        if (messages[hi].role === "user") {
          const nextMsg = messages[hi + 1];
          if (
            nextMsg &&
            nextMsg.role === "assistant" &&
            nextMsg.content &&
            !nextMsg.content.startsWith("エラーが発生しました: ") &&
            nextMsg.content !== "接続エラーが発生しました。"
          ) {
            pairs.push({ question: messages[hi].content, answer: nextMsg.content });
            hi += 2;
          } else {
            hi++;
          }
        } else {
          hi++;
        }
      }
      const history = pairs.slice(-3);

      const url = CONST.aiChatAnalysisWebSocketAPIURL + "?Authorization=" + idToken;
      const websocket = new WebSocket(url);
      wsRef.current = websocket;

      websocket.onopen = () => {
        websocket.send(
          JSON.stringify({
            message: msg,
            user_authority: loginUser?.authority || "",
            history,
          })
        );
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data) as Record<string, unknown>;

        if (data.type === "chunk") {
          streamingAnswerRef.current += (data.content as string) ?? "";
          setMessages((prev) => {
            const updated = [...prev];
            if (
              updated.length > 0 &&
              updated[updated.length - 1].role === "assistant"
            ) {
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                content: streamingAnswerRef.current,
              };
            } else {
              updated.push({
                role: "assistant",
                content: streamingAnswerRef.current,
              });
            }
            return updated;
          });
        } else if (data.type === "data") {
          const chartData = parseChartData(data);
          setMessages((prev) => {
            const updated = [...prev];
            if (
              updated.length > 0 &&
              updated[updated.length - 1].role === "assistant"
            ) {
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                chartData,
              };
            } else {
              updated.push({ role: "assistant", content: "", chartData });
            }
            return updated;
          });
        } else if (data.type === "done") {
          setLoading(false);
          if (
            data.suggested_questions &&
            Array.isArray(data.suggested_questions)
          ) {
            setSuggestedQuestions(data.suggested_questions as string[]);
          }
          setChatLogId((data.chat_log_id as number) ?? null);
          if (data.chat_log_id) {
            setFeedbackSent(false);
          }
          websocket.close();
        } else if (data.type === "answer") {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: (data.message as string) || "",
            },
          ]);
          setChatLogId(null);
          setLoading(false);
          websocket.close();
        } else if (data.type === "error") {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                "エラーが発生しました: " +
                ((data.message as string) || "不明なエラー"),
            },
          ]);
          setLoading(false);
          websocket.close();
        }
      };

      websocket.onerror = () => {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "接続エラーが発生しました。" },
        ]);
        setLoading(false);
      };

      websocket.onclose = () => {
        wsRef.current = null;
      };
    },
    [inputValue, loginUser, messages]
  );

  const handleFeedback = async (feedback: "good" | "bad") => {
    if (!chatLogId || feedbackSent) return;
    setFeedbackSent(true);
    try {
      await postDataChatFeedback(chatLogId, feedback);
    } catch (e) {
      console.error("feedback error", e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey && e.key === "Enter") {
      e.preventDefault();
      if (!loading && inputValue.trim()) {
        handleSend();
      }
    }
  };

  const PARTNER_EXAMPLE_QUESTIONS = ["商材別の契約件数は？", "ステータス別のトスアップ件数は？", "今日が希望架電日の案件は？"];
  const EXAMPLE_QUESTIONS_MAP: Record<string, string[]> = {
    マスターアカウント: ["今月のエントリー数は？", "パートナー別のエントリー数は？", "売上上位の商材は？"],
    スタッフアカウント: ["今月のエントリー数は？", "パートナー別のエントリー数は？", "商材別の契約件数は？"],
    パートナー: PARTNER_EXAMPLE_QUESTIONS,
    営業MGR: PARTNER_EXAMPLE_QUESTIONS,
    営業アカウント: PARTNER_EXAMPLE_QUESTIONS,
  };
  const exampleQuestions =
    EXAMPLE_QUESTIONS_MAP[loginUser?.authority ?? ""] ??
    ["今月のエントリー数は？", "パートナー別のエントリー数は？"];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: "16px", maxWidth: "760px" }} data-testid="data-chat-panel">
      {/* メッセージ領域 */}
      <Box data-testid="data-chat-body" sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: 1, py: 0.5 }}>
        {messages.length === 0 ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <SmartToyIcon
                sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }}
              />
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  mt: 0,
                  bgcolor: "var(--color-washi)",
                  borderRadius: "4px 12px 12px 12px",
                  maxWidth: "85%",
                }}
              >
                <Typography sx={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>
                  {"データ分析AIです。DBデータについて質問してください。"}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1 }}>
              {exampleQuestions.map((q) => (
                <Button
                  key={q}
                  variant="outlined"
                  fullWidth
                  sx={{
                    bgcolor: "white",
                    fontSize: "0.75rem",
                    justifyContent: "flex-start",
                    fontWeight: 400,
                  }}
                  onClick={() => handleSend(q)}
                >
                  {q}
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((msg, i) => (
            <Box
              key={i}
              data-testid="data-chat-msg"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: msg.role === "assistant" ? "flex-start" : "flex-end",
                mb: 1,
              }}
            >
              {msg.role === "assistant" ? (
                <SmartToyIcon
                  sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }}
                />
              ) : (
                <PersonIcon
                  sx={{ fontSize: 28, color: "primary.main", mb: 0.5 }}
                />
              )}
              {msg.content && (
                <Paper
                  elevation={0}
                  sx={{
                    display: "inline-block",
                    p: 0.75,
                    maxWidth: "90%",
                    bgcolor:
                      msg.role === "user" ? "var(--color-ai)" : "var(--color-white)",
                    color:
                      msg.role === "user"
                        ? "var(--color-white)"
                        : "var(--color-sumi)",
                    border: msg.role === "user" ? undefined : "1px solid var(--color-border)",
                    boxShadow: msg.role === "user" ? undefined : "var(--shadow-soft)",
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Box sx={{
                      fontSize: 'var(--fs-12)',
                      '& p': { margin: '0.25em 0' },
                      '& h1, & h2, & h3, & h4': { margin: '0.5em 0 0.25em', fontSize: '0.8rem', fontWeight: 'bold' },
                      '& ul, & ol': { margin: '0.25em 0', paddingLeft: '1.2em' },
                      '& li': { margin: '0.1em 0' },
                      '& code': { backgroundColor: 'rgba(0,0,0,0.06)', padding: '0.1em 0.3em', borderRadius: '3px', fontSize: '0.65rem' },
                      '& pre': { backgroundColor: 'rgba(0,0,0,0.06)', padding: '0.5em', borderRadius: '4px', overflow: 'auto', margin: '0.25em 0' },
                      '& pre code': { backgroundColor: 'transparent', padding: 0 },
                      '& strong': { fontWeight: 'bold' },
                      '& a': { color: 'primary.main' },
                      '& table': { borderCollapse: 'collapse', margin: '0.25em 0', fontSize: '0.65rem' },
                      '& th, & td': { border: '1px solid var(--color-border, #e5e5e0)', padding: '0.2em 0.4em' },
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", fontSize: "0.7rem" }}>
                      {msg.content}
                    </Typography>
                  )}
                </Paper>
              )}
              {msg.chartData && (
                <Box sx={{ width: "100%", mt: 0.5 }}>
                  <ChartRenderer data={msg.chartData} />
                </Box>
              )}
              {!loading &&
                chatLogId &&
                i === messages.length - 1 &&
                msg.role === "assistant" && (
                  <Box data-testid="data-chat-feedback-row" sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                    <IconButton
                      data-testid="data-chat-feedback-good"
                      size="small"
                      onClick={() => handleFeedback("good")}
                      disabled={feedbackSent}
                      color={feedbackSent ? "default" : "primary"}
                    >
                      <ThumbUpAltOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      data-testid="data-chat-feedback-bad"
                      size="small"
                      onClick={() => handleFeedback("bad")}
                      disabled={feedbackSent}
                      color={feedbackSent ? "default" : "error"}
                    >
                      <ThumbDownAltOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
            </Box>
          ))
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 0.5 }}>
            <CircularProgress size={16} />
          </Box>
        )}
        {suggestedQuestions.length > 0 && !loading && messages.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              px: 1,
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.65rem" }}
            >
              関連する質問:
            </Typography>
            {suggestedQuestions.map((q, idx) => (
              <Button
                key={idx}
                size="small"
                sx={{
                  bgcolor: "var(--color-ai-pale)",
                  color: "var(--color-ai)",
                  borderRadius: "999px",
                  fontSize: "11px",
                  px: "10px",
                  py: "3px",
                  minWidth: 0,
                  border: "none",
                  textTransform: "none",
                  justifyContent: "flex-start",
                  "&:hover": { bgcolor: "var(--color-ai-pale)", filter: "brightness(0.95)" },
                }}
                onClick={() => {
                  setSuggestedQuestions([]);
                  handleSend(q);
                }}
              >
                {q}
              </Button>
            ))}
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* 入力欄 */}
      <Box
        data-testid="data-chat-input"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          borderTop: "1px solid var(--color-border)",
          borderRadius: "var(--radius-md)",
          p: 1.5,
        }}
      >
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={4}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="データについて質問..."
          disabled={loading}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
          }}
        >
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "10px" }}>
            Ctrl+Enter で送信
          </Typography>
          <IconButton
            data-testid="data-chat-send"
            size="small"
            onClick={() => handleSend()}
            disabled={loading || !inputValue.trim()}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default DataChatPanel;
